/**
 * @author dhonehi
 * Данный composable подключается в каждом офере. При первом обращении к данному composable начинается загрузка корзины.
 * Корзина передается через поле items, которое может принимать функцию, котора должна возвращать объект или массив.
 *
 * Объект должен быть вида {id_товара: кол-во'}
 * Массив объектов должен быть вида [{id: id_товара, quantity: кол-во_товара}]
 *
 * Если в настройках корзины указано поле pollingRate, то корзина будет автоматически обновляться каждые pollingRate секунд.
 * При этом если загрузка корзины заняла больше времени, чем pollingRate, то в следующий раз корзина обновится только
 * после того как завершится текущая загрузка и после истечения pollingRate.
 *
 * При добавлении товара в корзину или изменении количества товара пользователем, вызывается функция onChangeOfferCount,
 * которая в свою очередь вызывает callback, передаваемый в настройках корзины. В данный callback передается payload,
 * который содержит информацию о товаре и два дополнительных параметра: currentQuantity (текущее количество товара) и
 * operationType ('inc' - увеличение, 'dec' - уменьшение, 'add' - добавление). Callback может вернуть число, которое будет
 * новым количеством товара. Если callback ничего не возвращает, то при operationType 'inc' или 'dec' количество товара
 * будет увеличено или уменьшено на 1 соответственно, если operationType 'add', то добавится один товар.
 * Таким образом клиент может сам регулировать количество добавляемых или удаляемых товаров.
 */

import { ref, watch } from '@vue/composition-api';

import ApiEventController from '@/init/ApiEventController';

const cartData = {
    cart: null,
    settings: null,
    initLoading: true,
};

let isLoadedCart = false;
let isLoadingCart = false;

let cartPollingIntervalId = null;

/** Загружает корзину*/
async function loadCart() {
    isLoadingCart = true;

    try {
        cartData.cart.value = null;

        if (typeof cartData.settings.items === 'function') {
            cartData.cart.value = await cartData.settings.items();
        } else {
            throw new Error('Cart items must be a function');
        }
    } catch (error) {
        console.error(error);
    } finally {
        isLoadingCart = false;
        isLoadedCart = true;
    }
}

/**
 * Считает количество текущего товара в корзине
 * @param {Object} offer - объект описания товара
 */
function getQuantityOffersInCart(offer) {
    if (!cartData.cart.value) {
        return 0;
    }

    if (Array.isArray(cartData.cart.value)) {
        return cartData.cart.value.find(cartItem => cartItem.id === offer.id)?.quantity || 0;
    }

    return cartData.cart.value[offer.id] || 0;
}

/** Запускает опрос корзины*/
function startCartPolling() {
    if (!cartData.settings.pollingRate) {
        loadCart();
        return;
    }

    let availableNextPolling = true;

    cartPollingIntervalId = setInterval(async () => {
        if (availableNextPolling) {
            availableNextPolling = false;

            await loadCart();

            availableNextPolling = true;
        }
    }, cartData.settings.pollingRate);
}

/** Останавливает опрос корзины*/
function stopCartPolling() {
    clearInterval(cartPollingIntervalId);
}

ApiEventController.on(ApiEventController.EVENTS.UPDATE_CART, (event) => {
    if (!cartData.cart) {
        return;
    }

    const detail = event.detail;

    if (detail) {
        cartData.cart.value = {
            ...cartData.cart.value,
            ...detail,
        };
    } else {
        loadCart();
    }
});

/**
 * Composable функция, которая отвечает за работу с корзиной
 * Все что находится внутри useOfferCartController, является уникальным состоянием для каждого компонента офера,
 * все что находится вне, является общим состоянием для всех компонентов офера
 * @param {Object} offer - объект описания товара
 * @param {Object} cartSettings - объект с настройками корзины из конфига
 */
export default function useOfferCartController(offer, cartSettings) {
    /** Счетчик количества товара в корзине*/
    const quantityOffersInCart = ref(0);
    /** Индикатор добавления товара в корзину*/
    const loadingAddToCart = ref(false);
    let loadingChangeOfferCount = false;

    /** Проверяем наличие настроек корзины*/
    const hasCartSettings = !!cartSettings?.items;
    /** Нужно ли опрашивать корзину*/
    const isNeedPollingCart = hasCartSettings && !!cartSettings.pollingRate;

    /** Загружаем корзину только один раз и только если она еще не загружена*/
    if (!isLoadedCart && !isLoadingCart) {
        /** Делаем корзину реактивной для того чтобы можно было повесить на нее watch*/
        cartData.cart = ref(null);
        cartData.initLoading = ref(true);
        cartData.settings = cartSettings;

        if (hasCartSettings) {
            loadCart().then(() => {
                if (isNeedPollingCart) {
                    startCartPolling();
                }

                cartData.initLoading.value = false;
            });
        }
    }

    /** Получаем количество товара в корзине после загрузки корзины*/
    quantityOffersInCart.value = getQuantityOffersInCart(offer);

    /**
     * Обработчик изменения количества товара в корзине
     * @param {String} operationType - тип операции (inc, dec или add)
     */
    const onChangeOfferCount = async (operationType) => {
        if (loadingChangeOfferCount) {
            return;
        }

        let newQuantityOffersInCart = quantityOffersInCart.value;
        let oldQuantityOffersInCart = quantityOffersInCart.value;
        let delta = 0;
      
        switch (operationType) {
        case 'inc':
            ++newQuantityOffersInCart;
            loadingChangeOfferCount = true;
            break;
        case 'dec':
            --newQuantityOffersInCart;
            loadingChangeOfferCount = true;
            break;
        case 'add':
            loadingAddToCart.value = true;
            newQuantityOffersInCart = 1;
            break;
        case 'removeAll':
            newQuantityOffersInCart = 0;
        }

        try {
            stopCartPolling();

            const result = await cartData.settings.onChangeOfferCount({
                ...offer,
                operationType,
                currentQuantity: quantityOffersInCart.value,
            });

            quantityOffersInCart.value = typeof result === 'number' ? result : newQuantityOffersInCart;

            window.sbEvents.pushEvent('UPDATE_CART', { [offer.id]: quantityOffersInCart.value });

            delta = Math.abs(quantityOffersInCart.value - oldQuantityOffersInCart);

            return { quantity: quantityOffersInCart.value, oldQuantity: oldQuantityOffersInCart, delta };
        } catch (error) {
            console.error(error);
        } finally {
            loadingAddToCart.value = false;
            loadingChangeOfferCount = false;

            if (isNeedPollingCart) {
                startCartPolling();
            }
        }
    };

    /** Обработчик перехода в корзину*/
    const onGoToCart = () => {
        try {
            if (cartData.settings?.onGoToCart) {
                cartData.settings.onGoToCart(offer);
            }
        } catch (error) {
            console.error(error);
        }
    };

    watch(cartData.cart, () => {
        if (cartData.cart.value) {
            quantityOffersInCart.value = getQuantityOffersInCart(offer);
        }
    });

    return {
        quantityOffersInCart,
        loadingAddToCart,
        initLoading: cartData.initLoading,
        onChangeOfferCount: hasCartSettings && cartData.settings.onChangeOfferCount ?
            onChangeOfferCount :
            () => {
            },
        onGoToCart: hasCartSettings && cartData.settings.onGoToCart ?
            onGoToCart :
            () => {
            },
    };
}
